/* ChillBlimp Cold — theme.js */
(function () {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------
     Utilities
     ------------------------------------------------------------------ */
  function fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` }
    };
  }

  function formatMoney(cents) {
    const fmt = window.themeMoneyFormat || '{{amount}}';
    const value = (cents / 100).toFixed(2);
    const [int, dec] = value.split('.');
    const intFmt = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace('{{amount}}', `${intFmt}.${dec}`)
      .replace('{{amount_no_decimals}}', intFmt)
      .replace('{{amount_with_comma_separator}}', `${int.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec}`)
      .replace('{{amount_no_decimals_with_comma_separator}}', int.replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
      .replace('{{amount_with_space_separator}}', `${int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${dec}`);
  }

  /*
    Animated horizontal scroll that works inside scroll-snap containers.
    Native `scrollTo({ behavior: 'smooth' })` is cancelled by Chromium when the
    track has `scroll-snap-type: mandatory` (it snaps straight back to the
    current slide), so we temporarily disable snapping, animate scrollLeft
    ourselves, then restore it once we've landed exactly on the target.
  */
  function animateScroll(track, left, duration = 380) {
    const max = Math.max(0, track.scrollWidth - track.clientWidth);
    left = Math.max(0, Math.min(max, Math.round(left)));
    const start = track.scrollLeft;
    const dist = left - start;
    if (track._scrollRaf) cancelAnimationFrame(track._scrollRaf);
    if (Math.abs(dist) < 1) return;
    if (reduceMotion) { track.scrollLeft = left; return; }
    if (track._snapRestore === undefined) track._snapRestore = track.style.scrollSnapType;
    track.style.scrollSnapType = 'none';
    const t0 = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const frame = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      track.scrollLeft = start + dist * ease(p);
      if (p < 1) {
        track._scrollRaf = requestAnimationFrame(frame);
      } else {
        track.scrollLeft = left;
        track.style.scrollSnapType = track._snapRestore;
        track._snapRestore = undefined;
        track._scrollRaf = null;
      }
    };
    track._scrollRaf = requestAnimationFrame(frame);
  }

  const toastEl = (() => {
    const el = document.createElement('div');
    el.className = 'toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    document.body.appendChild(el);
    return el;
  })();
  let toastTimer;
  function toast(message, type = 'success') {
    const icon = type === 'error'
      ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>'
      : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6 9 17l-5-5"/></svg>';
    toastEl.innerHTML = icon + '<span></span>';
    toastEl.querySelector('span').textContent = message;
    toastEl.className = 'toast is-visible' + (type === 'error' ? ' toast--error' : '');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 3200);
  }

  function trapFocus(container) {
    const focusable = $$('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])', container)
      .filter((el) => el.offsetParent !== null);
    if (!focusable.length) return () => {};
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  }

  /* ------------------------------------------------------------------
     Drawers (menu / cart / search)
     ------------------------------------------------------------------ */
  const drawers = {};
  let activeDrawer = null;
  let releaseTrap = () => {};
  let lastFocus = null;

  function openDrawer(id) {
    const drawer = drawers[id];
    if (!drawer) return;
    if (activeDrawer && activeDrawer !== drawer) closeDrawer();
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
    activeDrawer = drawer;
    releaseTrap = trapFocus(drawer);
    const focusTarget = drawer.querySelector('[data-autofocus]') || drawer.querySelector('.drawer__close');
    setTimeout(() => focusTarget && focusTarget.focus(), 60);
    drawer.dispatchEvent(new CustomEvent('drawer:open'));
  }
  function closeDrawer() {
    if (!activeDrawer) return;
    activeDrawer.classList.remove('is-open');
    activeDrawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');
    releaseTrap();
    activeDrawer = null;
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function initDrawers() {
    $$('[data-drawer]').forEach((drawer) => {
      drawers[drawer.dataset.drawer] = drawer;
      drawer.setAttribute('aria-hidden', 'true');
      $$('[data-drawer-close]', drawer).forEach((btn) => btn.addEventListener('click', closeDrawer));
      const overlay = $('.drawer__overlay', drawer);
      if (overlay) overlay.addEventListener('click', closeDrawer);
    });
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-drawer-open]');
      if (!trigger) return;
      const id = trigger.dataset.drawerOpen;
      if (!drawers[id]) return;
      e.preventDefault();
      openDrawer(id);
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawer(); });
  }

  /* ------------------------------------------------------------------
     Reveal on scroll
     ------------------------------------------------------------------ */
  function initReveal() {
    const els = $$('.reveal, .reveal-group');
    if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-visible')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach((el) => io.observe(el));
  }

  /* ------------------------------------------------------------------
     Parallax banners
     ------------------------------------------------------------------ */
  function initParallax() {
    const banners = $$('.banner--parallax');
    if (!banners.length || reduceMotion) return;
    let ticking = false;
    function update() {
      banners.forEach((b) => {
        const img = $('.banner__media img', b);
        if (!img) return;
        const rect = b.getBoundingClientRect();
        const vh = window.innerHeight;
        if (rect.bottom < 0 || rect.top > vh) return;
        const progress = (rect.top + rect.height / 2 - vh / 2) / vh;
        img.style.transform = `translateY(${progress * -12}%)`;
      });
      ticking = false;
    }
    window.addEventListener('scroll', () => { if (!ticking) { requestAnimationFrame(update); ticking = true; } }, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------
     Sliders (scroll-snap tracks with prev/next)
     The product gallery has its own controller (initGallery) and is skipped here.
     ------------------------------------------------------------------ */
  function initSliders() {
    $$('[data-slider]').forEach((slider) => {
      if (slider.hasAttribute('data-gallery') || slider._sliderInit) return;
      const track = $('[data-slider-track]', slider);
      if (!track) return;
      slider._sliderInit = true;
      const step = () => {
        const slide = track.querySelector('[data-slider-slide], .slider__slide');
        return slide ? slide.getBoundingClientRect().width + parseFloat(getComputedStyle(track).gap || 0) : track.clientWidth;
      };
      $$('[data-slider-prev]', slider).forEach((b) => b.addEventListener('click', () => animateScroll(track, track.scrollLeft - step())));
      $$('[data-slider-next]', slider).forEach((b) => b.addEventListener('click', () => animateScroll(track, track.scrollLeft + step())));
    });
  }

  /* ------------------------------------------------------------------
     Header cart count + Cart API
     ------------------------------------------------------------------ */
  function updateCartCount(count) {
    $$('[data-cart-count]').forEach((el) => { el.textContent = count > 0 ? count : ''; el.dataset.count = count; });
  }

  async function refreshCartSections() {
    // Re-render cart drawer + cart page from section rendering API
    const ids = [];
    if ($('#CartDrawer')) ids.push('cart-drawer');
    if ($('#MainCart')) ids.push($('#MainCart').dataset.sectionId);
    if (!ids.length) return;
    const res = await fetch(`${window.routes.cart_url}?sections=${ids.join(',')}&t=${Date.now()}`);
    const sections = await res.json();
    Object.entries(sections).forEach(([id, html]) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      if (id === 'cart-drawer') {
        const fresh = $('#CartDrawer .drawer__panel', doc);
        const current = $('#CartDrawer .drawer__panel');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
        $$('[data-drawer-close]', $('#CartDrawer')).forEach((btn) => btn.addEventListener('click', closeDrawer));
      } else {
        const fresh = $('#MainCart', doc);
        const current = $('#MainCart');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
      }
    });
    const cartRes = await fetch(`${window.routes.cart_url}.js`);
    const cart = await cartRes.json();
    updateCartCount(cart.item_count);
  }

  async function changeCartLine(line, quantity, errorTarget) {
    const buttons = $$('.cart-item button');
    buttons.forEach((b) => (b.disabled = true));
    try {
      const res = await fetch(window.routes.cart_change_url, { ...fetchConfig(), body: JSON.stringify({ line, quantity, sections: [] }) });
      const data = await res.json();
      if (data.status) throw new Error(data.message || data.description || window.cartStrings.error);
      await refreshCartSections();
    } catch (err) {
      if (errorTarget) errorTarget.textContent = err.message;
      else toast(err.message, 'error');
      buttons.forEach((b) => (b.disabled = false));
    }
  }

  function initCart() {
    document.addEventListener('click', (e) => {
      const plus = e.target.closest('[data-cart-plus]');
      const minus = e.target.closest('[data-cart-minus]');
      const remove = e.target.closest('[data-cart-remove]');
      const item = e.target.closest('[data-cart-item]');
      if (!item) return;
      const line = Number(item.dataset.line);
      const input = $('[data-cart-qty]', item);
      const err = $('.cart-item__error', item);
      if (plus) { e.preventDefault(); changeCartLine(line, Number(input.value) + 1, err); }
      if (minus) { e.preventDefault(); changeCartLine(line, Math.max(0, Number(input.value) - 1), err); }
      if (remove) { e.preventDefault(); changeCartLine(line, 0, err); }
    });
    document.addEventListener('change', (e) => {
      const input = e.target.closest('[data-cart-qty]');
      if (!input) return;
      const item = input.closest('[data-cart-item]');
      changeCartLine(Number(item.dataset.line), Math.max(0, Number(input.value)), $('.cart-item__error', item));
    });
    // Cart note
    let noteTimer;
    document.addEventListener('input', (e) => {
      const note = e.target.closest('[data-cart-note]');
      if (!note) return;
      clearTimeout(noteTimer);
      noteTimer = setTimeout(() => {
        fetch(window.routes.cart_update_url, { ...fetchConfig(), body: JSON.stringify({ note: note.value }) });
      }, 500);
    });
  }

  /* ------------------------------------------------------------------
     Product forms (add to cart) — main product + quick add
     ------------------------------------------------------------------ */
  async function addToCart(form, button) {
    const errorEl = form.querySelector('.product__error');
    if (errorEl) { errorEl.classList.remove('is-visible'); errorEl.textContent = ''; }
    button.classList.add('loading');
    button.setAttribute('aria-disabled', 'true');
    const formData = new FormData(form);
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;
    try {
      const res = await fetch(window.routes.cart_add_url, config);
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message || window.cartStrings.error);
      await refreshCartSections();
      if (window.themeCartType === 'drawer' && drawers.cart) {
        openDrawer('cart');
      } else if (window.themeCartType === 'page') {
        window.location.href = window.routes.cart_url;
        return;
      } else {
        toast(data.product_title ? `${data.product_title} added to cart` : 'Added to cart');
      }
    } catch (err) {
      if (errorEl) { errorEl.textContent = err.message; errorEl.classList.add('is-visible'); }
      else toast(err.message, 'error');
    } finally {
      button.classList.remove('loading');
      button.removeAttribute('aria-disabled');
    }
  }

  function initProductForms() {
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form.product-form');
      if (!form) return;
      e.preventDefault();
      const button = form.querySelector('[type="submit"]');
      if (button && (button.disabled || button.getAttribute('aria-disabled') === 'true')) return;
      addToCart(form, button);
    });
    // Quick add buttons on cards
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      e.preventDefault();
      const id = btn.dataset.quickAdd;
      btn.disabled = true;
      try {
        const res = await fetch(window.routes.cart_add_url, { ...fetchConfig(), body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }) });
        const data = await res.json();
        if (data.status) throw new Error(data.description || data.message || window.cartStrings.error);
        await refreshCartSections();
        if (window.themeCartType === 'drawer' && drawers.cart) openDrawer('cart');
        else if (window.themeCartType === 'page') { window.location.href = window.routes.cart_url; return; }
        else toast('Added to cart');
      } catch (err) {
        toast(err.message, 'error');
      } finally {
        btn.disabled = false;
      }
    });
    // Quantity steppers (product page)
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-qty-plus], [data-qty-minus]');
      if (!btn) return;
      const wrap = btn.closest('.quantity');
      const input = wrap && wrap.querySelector('input');
      if (!input) return;
      const min = Number(input.min || 1);
      const max = input.max ? Number(input.max) : Infinity;
      let v = Number(input.value || min);
      v = btn.hasAttribute('data-qty-plus') ? v + 1 : v - 1;
      input.value = Math.min(max, Math.max(min, v));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ------------------------------------------------------------------
     Variant picker
     ------------------------------------------------------------------ */
  function initVariantPickers() {
    $$('[data-variant-picker]').forEach((picker) => {
      if (picker._pickerInit) return;
      picker._pickerInit = true;
      const sectionId = picker.dataset.section;
      const section = (picker.parentElement && picker.parentElement.closest(`[data-section="${sectionId}"]`)) || document;
      const json = $(`[data-variants-json]`, section);
      if (!json) return;
      const variants = JSON.parse(json.textContent);
      const form = $('form.product-form', section);
      const idInput = form && form.querySelector('input[name="id"]');
      const button = form && form.querySelector('[type="submit"]');
      const buttonText = button && button.querySelector('span');
      const priceWrap = $('[data-product-price]', section);
      const skuEl = $('[data-product-sku]', section);
      const inventoryEl = $('[data-product-inventory]', section);
      const gallery = $('[data-gallery]', section);
      const optionCount = Number(picker.dataset.optionCount || 1);

      function selectedOptions() {
        const values = [];
        for (let i = 1; i <= optionCount; i++) {
          const radio = picker.querySelector(`input[type="radio"][data-option-index="${i}"]:checked`);
          const select = picker.querySelector(`select[data-option-index="${i}"]`);
          values.push(radio ? radio.value : select ? select.value : null);
        }
        return values;
      }

      function findVariant(values) {
        return variants.find((v) => v.options.every((o, i) => o === values[i]));
      }

      function updateAvailability(values) {
        for (let i = 1; i <= optionCount; i++) {
          const inputs = $$(`[data-option-index="${i}"]`, picker);
          inputs.forEach((input) => {
            const isSelect = input.tagName === 'SELECT';
            const opts = isSelect ? Array.from(input.options) : [input];
            opts.forEach((opt) => {
              const testValues = values.slice();
              testValues[i - 1] = opt.value;
              // Check any variant that matches previous options + this one
              const match = variants.find((v) => v.options.every((o, idx) => idx >= i ? true : o === testValues[idx]));
              const available = variants.some((v) => v.available && v.options.every((o, idx) => idx >= i ? true : o === testValues[idx]));
              const label = isSelect ? null : picker.querySelector(`label[for="${opt.id}"]`);
              if (label) label.classList.toggle('variant-picker__value--unavailable', !available || !match);
              if (isSelect) opt.textContent = opt.dataset.value + (!available ? ` — ${window.variantStrings.soldOut}` : '');
            });
          });
        }
      }

      function render(isInitial) {
        const values = selectedOptions();
        const variant = findVariant(values);
        updateAvailability(values);
        // Update selected value labels
        for (let i = 1; i <= optionCount; i++) {
          const label = picker.querySelector(`[data-selected-value="${i}"]`);
          if (label) label.textContent = values[i - 1] || '';
        }
        if (!variant) {
          if (button) { button.disabled = true; if (buttonText) buttonText.textContent = window.variantStrings.unavailable; }
          return;
        }
        if (idInput) idInput.value = variant.id;
        if (button) {
          button.disabled = !variant.available;
          if (buttonText) buttonText.textContent = variant.available ? window.variantStrings.addToCart : window.variantStrings.soldOut;
        }
        if (priceWrap) {
          const current = priceWrap.querySelector('[data-price-current]');
          const compare = priceWrap.querySelector('[data-price-compare]');
          if (current) current.textContent = formatMoney(variant.price);
          if (compare) {
            if (variant.compare_at_price && variant.compare_at_price > variant.price) { compare.textContent = formatMoney(variant.compare_at_price); compare.hidden = false; }
            else compare.hidden = true;
          }
        }
        if (skuEl) { skuEl.textContent = variant.sku || ''; skuEl.hidden = !variant.sku; }
        if (inventoryEl) {
          const qty = variant.inventory_quantity;
          const managed = variant.inventory_management;
          if (!managed) { inventoryEl.hidden = true; }
          else {
            inventoryEl.hidden = false;
            inventoryEl.classList.remove('product__inventory--low', 'product__inventory--out');
            if (!variant.available) { inventoryEl.textContent = window.variantStrings.soldOut; inventoryEl.classList.add('product__inventory--out'); }
            else if (qty > 0 && qty <= 5) { inventoryEl.textContent = inventoryEl.dataset.lowText.replace('[qty]', qty); inventoryEl.classList.add('product__inventory--low'); }
            else inventoryEl.textContent = inventoryEl.dataset.inText;
          }
        }
        if (gallery && variant.featured_media) {
          if (typeof gallery._goToMedia === 'function') gallery._goToMedia(variant.featured_media.id, isInitial === true);
        }
        // Update URL
        if (window.history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      }

      picker.addEventListener('change', () => render(false));
      render(true);
    });
  }

  /* ------------------------------------------------------------------
     Product gallery (main slider + thumbnails + prev/next)
     ------------------------------------------------------------------ */
  function initGallery() {
    $$('[data-gallery]').forEach((gallery) => {
      if (gallery._galleryInit) return;
      const track = $('[data-gallery-track]', gallery);
      if (!track) return;
      gallery._galleryInit = true;

      const items = $$('.product__gallery-item', track);
      const thumbs = $$('[data-thumb]', gallery);
      const thumbsWrap = $('.product__thumbs', gallery);
      const prevBtn = $('[data-slider-prev]', gallery);
      const nextBtn = $('[data-slider-next]', gallery);
      const count = items.length;
      let current = 0;

      function setActive(index) {
        current = index;
        thumbs.forEach((t, i) => t.setAttribute('aria-current', i === index ? 'true' : 'false'));
        // Keep the active thumbnail in view inside the (horizontally scrolling) thumb strip
        const activeThumb = thumbs[index];
        if (thumbsWrap && activeThumb) {
          const target = activeThumb.offsetLeft - (thumbsWrap.clientWidth - activeThumb.offsetWidth) / 2;
          thumbsWrap.scrollTo({ left: Math.max(0, target), behavior: reduceMotion ? 'auto' : 'smooth' });
        }
        // Pause any video that has scrolled out of view
        items.forEach((it, i) => {
          const v = it.querySelector('video');
          if (v && i !== index && !v.paused) v.pause();
        });
      }

      function goTo(index, instant) {
        if (!count) return;
        index = ((index % count) + count) % count; // wrap around
        const target = items[index];
        if (instant) {
          if (track._scrollRaf) cancelAnimationFrame(track._scrollRaf);
          track.scrollLeft = target.offsetLeft;
        } else {
          animateScroll(track, target.offsetLeft);
        }
        setActive(index);
      }

      thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => goTo(i)));
      if (prevBtn) prevBtn.addEventListener('click', () => goTo(current - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => goTo(current + 1));

      // Keyboard navigation when the gallery has focus
      gallery.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current + 1); }
      });

      // Sync active thumb while the user swipes / scrolls the track
      let raf;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          if (!track.clientWidth) return;
          const idx = Math.max(0, Math.min(count - 1, Math.round(track.scrollLeft / track.clientWidth)));
          if (idx !== current) setActive(idx);
        });
      }, { passive: true });

      // Keep the current slide aligned after the layout changes size
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => { if (items[current]) track.scrollLeft = items[current].offsetLeft; }, 120);
      });

      // Public hook used by the variant picker
      gallery._goToMedia = (mediaId, instant) => {
        const idx = items.findIndex((it) => it.dataset.mediaId === String(mediaId));
        if (idx > -1 && idx !== current) goTo(idx, instant);
      };

      setActive(0);
    });
  }

  /* ------------------------------------------------------------------
     Predictive search
     ------------------------------------------------------------------ */
  function initPredictiveSearch() {
    const forms = $$('[data-predictive-search]');
    forms.forEach((wrap) => {
      const input = $('input[type="search"]', wrap);
      const results = $('[data-predictive-results]', wrap);
      if (!input || !results) return;
      let timer;
      let controller;
      input.addEventListener('input', () => {
        clearTimeout(timer);
        const q = input.value.trim();
        if (q.length < 2) { results.innerHTML = ''; return; }
        timer = setTimeout(async () => {
          if (controller) controller.abort();
          controller = new AbortController();
          try {
            const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product,collection,page,article&resources[limit]=6&section_id=predictive-search`;
            const res = await fetch(url, { signal: controller.signal });
            const html = await res.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const inner = $('#shopify-section-predictive-search', doc);
            results.innerHTML = inner ? inner.innerHTML : '';
          } catch (e) { /* aborted */ }
        }, 250);
      });
    });
  }

  /* ------------------------------------------------------------------
     Collection filters (facets) — submit on change
     ------------------------------------------------------------------ */
  function initFacets() {
    const form = $('[data-facets-form]');
    if (!form) return;
    let timer;
    form.addEventListener('change', () => { clearTimeout(timer); timer = setTimeout(() => form.requestSubmit ? form.requestSubmit() : form.submit(), 250); });
    const toggle = $('[data-facets-toggle]');
    const facets = $('[data-facets]');
    if (toggle && facets) toggle.addEventListener('click', () => { facets.classList.toggle('is-open'); toggle.setAttribute('aria-expanded', facets.classList.contains('is-open')); });
    const sort = $('[data-sort-by]');
    if (sort) sort.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }

  /* ------------------------------------------------------------------
     Localization forms
     ------------------------------------------------------------------ */
  function initLocalization() {
    $$('[data-localization] select').forEach((select) => {
      select.addEventListener('change', () => select.form.submit());
    });
  }

  /* ------------------------------------------------------------------
     Share button
     ------------------------------------------------------------------ */
  function initShare() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-share]');
      if (!btn) return;
      const url = btn.dataset.share || window.location.href;
      try {
        if (navigator.share) await navigator.share({ url, title: document.title });
        else { await navigator.clipboard.writeText(url); toast('Link copied'); }
      } catch (err) { /* cancelled */ }
    });
  }

  /* ------------------------------------------------------------------
     Theme editor hooks
     ------------------------------------------------------------------ */
  function initEditor() {
    if (!window.Shopify || !window.Shopify.designMode) return;
    document.addEventListener('shopify:section:load', () => { initSliders(); initGallery(); initVariantPickers(); initReveal(); });
    document.addEventListener('shopify:section:select', (e) => {
      if (e.target.id === 'shopify-section-cart-drawer') openDrawer('cart');
    });
    document.addEventListener('shopify:section:deselect', (e) => {
      if (e.target.id === 'shopify-section-cart-drawer') closeDrawer();
    });
  }

  /* ------------------------------------------------------------------
     Init
     ------------------------------------------------------------------ */
  function init() {
    initDrawers();
    initReveal();
    initParallax();
    initSliders();
    initCart();
    initProductForms();
    initGallery();          // must run before variant pickers (they call gallery._goToMedia)
    initVariantPickers();
    initPredictiveSearch();
    initFacets();
    initLocalization();
    initShare();
    initEditor();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.ChillBlimp = { openDrawer, closeDrawer, toast, refreshCartSections };
})();
